import {
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
} from '@remix-run/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FeedEntry } from '~/services/layout';
import type { loader as rootLoader } from '~/root.tsx';
import localforage from 'localforage';
import { ActionBus } from '~/hooks/actions/ActionBus.ts';
import { useLoadFeed } from '~/hooks/use-feed.ts';
import { useIsLoggedIn } from '~/hooks/use-is-logged-in.ts';
import { useLocalStorageSuffix } from '~/hooks/use-local-storage-suffix.ts';
import { getNumber, resolveAudioMimeType } from './audio-player-utils';
import { ExpandedAudioPlayer } from './expanded-audio-player';
import { MiniAudioPlayer } from './mini-audio-player';
import { QueuePanel } from './queue-panel';
import { useAudioPlayerPopupMenu } from './use-audio-player-popup-menu';
import { dispatchHidePopupMenu } from '~/services/plugins/popup-menu/components/host/index.ts';
import { useAudioPlayerAudioSync } from './hooks/use-audio-player-audio-sync';
import { useAudioPlayerDerived } from './hooks/use-audio-player-derived';
import { useAudioPlayerOpenEvent } from './hooks/use-audio-player-open-event';
import { useAudioPlayerState } from './hooks/use-audio-player-state';
import {
  resolveAdjacentQueueEntry,
  resolveAdjacentSiblingEntry,
  type QueueTransportDirection,
} from './queue-playback';
import {
  AUDIO_PLAYER_CLOSE_EVENT,
  AUDIO_PLAYER_COLLAPSE_EXPANDED_EVENT,
  dispatchAudioPlayerCollapseExpanded,
  resolveAudioPlayerPlayableEntry,
} from '~/utils/audio-player.ts';
import { isRecord } from '~/services/layout/layout.ts';
import {
  QueueManager,
  setupClientQueue,
  QUEUE_CHANGED_EVENT,
} from '~/services/queue/index.ts';
import { defaultCtxGetter } from '~/hooks/actions/utils/prepare-action-with-context.ts';
import { ZAPP_STORAGE_UPDATED_EVENT } from '~/hooks/actions/defaultActions/sessionStorageSet.ts';

function getStoredProfileString(): string | null {
  return (
    defaultCtxGetter('user_account:profile') ??
    defaultCtxGetter('user_account.profile')
  );
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

const PREVIOUS_TRACK_RESTART_THRESHOLD_SECONDS = 0.01;

type PreviousTrackIntent = 'restart-current' | 'play-previous';

export function shouldRestartCurrentTrackOnPreviousTap({
  currentTime,
}: {
  currentTime: number;
}): boolean {
  if (!Number.isFinite(currentTime)) {
    return false;
  }

  return currentTime > PREVIOUS_TRACK_RESTART_THRESHOLD_SECONDS;
}

export function resolvePreviousTrackIntent({
  currentTime,
  hasPendingPreviousAfterRestart,
}: {
  currentTime: number;
  hasPendingPreviousAfterRestart: boolean;
}): PreviousTrackIntent {
  if (hasPendingPreviousAfterRestart) {
    return 'play-previous';
  }

  if (
    shouldRestartCurrentTrackOnPreviousTap({
      currentTime,
    })
  ) {
    return 'restart-current';
  }

  return 'play-previous';
}

export function shouldRestoreQueueOriginOnBoundary({
  direction,
  hasQueueItems,
  hasCurrentQueueItem,
  hasAdjacentQueueEntry,
  hasOriginEntry,
}: {
  direction: QueueTransportDirection;
  hasQueueItems: boolean;
  hasCurrentQueueItem: boolean;
  hasAdjacentQueueEntry: boolean;
  hasOriginEntry: boolean;
}): boolean {
  return (
    direction === 'previous' &&
    hasQueueItems &&
    hasCurrentQueueItem &&
    !hasAdjacentQueueEntry &&
    hasOriginEntry
  );
}

export function getIsAddToPlaylistMutationSuccess({
  action,
  entryId,
}: {
  action: { type?: string; options?: unknown };
  entryId?: string;
}): boolean {
  if (action.type !== 'sendCloudEvent_success') {
    return false;
  }

  if (!entryId) {
    return false;
  }

  if (!isRecord(action.options)) {
    return false;
  }

  const sourceAction = action.options.action;

  if (!isRecord(sourceAction) || sourceAction.type !== 'sendCloudEvent') {
    return false;
  }

  if (!isRecord(sourceAction.options)) {
    return false;
  }

  const cloudEventType = getStringValue(sourceAction.options.type);

  if (cloudEventType !== 'com.applicaster.collection.toggle.v1') {
    return false;
  }

  const cloudEventData = sourceAction.options.data;

  if (!isRecord(cloudEventData)) {
    return false;
  }

  const itemId = getStringValue(cloudEventData.itemId);

  return itemId === entryId;
}

/** Coordinates audio playback state and renders mini/expanded player shells. */
export function AudioPlayerRoot(): JSX.Element | null {
  const { audioPlayerConfig, queueWebConfig } =
    useLoaderData<typeof rootLoader>();

  const [queueItems, setQueueItems] = useState<FeedEntry[]>([]);
  const addToPlaylistButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasPendingPreviousAfterRestartRef = useRef<boolean>(false);
  const playbackRequestIdRef = useRef<number>(0);
  const { isLoggedIn } = useIsLoggedIn();
  const localStorageSuffix = useLocalStorageSuffix(isLoggedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const favoriteActionFetcher = useFetcher({
    key: 'audio-player-favorite-action',
  });

  useLoadFeed('favorites', true);

  const general = audioPlayerConfig.general as Record<string, any>;

  const skipSeconds = getNumber(general.skip_value_seconds, 10);

  const {
    audioRef,
    entry,
    setEntry,
    isExpanded,
    setIsExpanded,
    isPlaying,
    setIsPlaying,
    isMuted,
    isQueueOpen,
    setIsQueueOpen,
    isSpeedMenuOpen,
    setIsSpeedMenuOpen,
    isFavorited,
    setIsFavorited,
    isPlaylistAdded,
    setIsPlaylistAdded,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    volume,
    playbackRate,
    playbackSpeedOpen,
    setPlaybackSpeedOpen,
    togglePlayback,
    closePlayer,
    handleSeek,
    formatTime,
    handleSkipBack,
    handleSkipAhead,
    handleVolumeChange,
    toggleMute,
    selectPlaybackRate,
  } = useAudioPlayerState({ skipSeconds });

  const { showMoreMenu, showAddToPlaylist } = useAudioPlayerPopupMenu(entry);

  const dismissPopupMenu = useCallback(() => {
    dispatchHidePopupMenu();
  }, []);

  const closeSpeedOverlay = useCallback(() => {
    setPlaybackSpeedOpen(false);
    setIsSpeedMenuOpen(false);
  }, [setPlaybackSpeedOpen, setIsSpeedMenuOpen]);

  const setPlaybackSpeedOpenExclusive = useCallback(
    (value: boolean | ((prevState: boolean) => boolean)) => {
      setPlaybackSpeedOpen((prev) => {
        const next =
          typeof value === 'function'
            ? (value as (prevState: boolean) => boolean)(prev)
            : value;

        setIsSpeedMenuOpen(next);

        if (next) {
          setIsQueueOpen(false);
          dismissPopupMenu();
        }

        return next;
      });
    },
    [dismissPopupMenu, setIsQueueOpen, setIsSpeedMenuOpen, setPlaybackSpeedOpen]
  );

  const handleOpenMoreMenuExclusive = useCallback(
    (triggerElement: HTMLElement | null): void => {
      setIsQueueOpen(false);
      closeSpeedOverlay();
      showMoreMenu(triggerElement);
    },
    [closeSpeedOverlay, setIsQueueOpen, showMoreMenu]
  );

  useAudioPlayerOpenEvent({
    setEntry,
    setIsPlaying,
    setIsExpanded,
    requestIdRef: playbackRequestIdRef,
  });

  useAudioPlayerAudioSync({
    audioRef,
    entry,
    isPlaying,
    isMuted,
    volume,
    playbackRate,
    setIsSpeedMenuOpen,
    setPlaybackSpeedOpen,
    setIsFavorited,
    setIsPlaylistAdded,
    setCurrentTime,
    setDuration,
    setIsPlaying,
  });

  const {
    speedOptions,
    hasSpeedControl,
    hasQueue,
    hasAddToPlaylistAction,
    showMiniFavoriteDesktop,
    showMiniAddDesktop,
    showFullFavoriteMobile,
    miniTitle,
    miniArtist,
    fullTitle,
    fullArtist,
    fullPlaylistName,
    miniImageSrc,
    fullImageSrc,
    miniImageCornerRadius,
    miniImagePlaceholderColor,
    miniImageBorderColor,
    miniProgressWidth,
    miniSideContainerWidth,
    miniTitleColor,
    miniArtistColor,
    barProgressColor,
    barTotalColor,
    barHandleColor,
    elapsedLabelColor,
    totalLabelColor,
    miniTitleTextStyle,
    miniArtistTextStyle,
    fullTitleTextStyle,
    fullArtistTextStyle,
    fullPlaylistTextStyle,
    timeLabelTextStyle,
    playbackButtonInteractionStyle,
    iconAssets,
    queueTitle,
    queuePanelLabels,
    queuePanelAssets,
    queuePanelItemConfig,
    queuePanelNowPlayingConfig,
    queuePanelTextTransforms,
    fullImageCornerRadius,
    fullImagePlaceholderColor,
    fullImageBorderColor,
    fullContainerClassName,
    miniContainerClassName,
    remaining,
    fullMaxWidthTablet,
    fullTextGutter,
  } = useAudioPlayerDerived({
    audioPlayerConfig,
    queueWebConfig,
    entry,
    currentTime,
    duration,
  });

  const handleToggleQueueExclusive = useCallback(() => {
    if (!hasQueue) return;

    setIsQueueOpen((prev) => {
      const next = !prev;

      if (next) {
        setIsExpanded(true);
        closeSpeedOverlay();
        dismissPopupMenu();
      }

      return next;
    });
  }, [
    closeSpeedOverlay,
    dismissPopupMenu,
    hasQueue,
    setIsExpanded,
    setIsQueueOpen,
  ]);

  const refreshQueueItems = useCallback(async () => {
    setupClientQueue();
    const upcoming = await QueueManager.instance.getUpcomingQueue();
    setQueueItems(upcoming);
  }, []);

  const handleClearQueue = useCallback(async () => {
    setupClientQueue();
    await QueueManager.instance.clearQueue();
    setQueueItems([]);
  }, []);

  const handleRemoveQueueItem = useCallback(
    async ({
      itemId,
      queueInstanceId,
    }: {
      itemId: string;
      queueInstanceId?: string;
    }) => {
      setupClientQueue();
      await QueueManager.instance.removeFromQueue({
        id: itemId,
        title: '',
        extensions: {
          queueItemId: queueInstanceId,
        },
      });
      const upcoming = await QueueManager.instance.getUpcomingQueue();
      setQueueItems(upcoming);
    },
    []
  );

  const handleReorderQueueItems = useCallback(
    async ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) => {
      setupClientQueue();
      const upcoming = await QueueManager.instance.getUpcomingQueue();
      if (
        fromIndex < 0 ||
        fromIndex >= upcoming.length ||
        toIndex < 0 ||
        toIndex >= upcoming.length
      ) {
        return;
      }
      const reordered = [...upcoming];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      const itemIds = reordered.map((item) =>
        String(item.extensions?.queueItemId || item.id)
      );
      await QueueManager.instance.move(itemIds);
      setQueueItems(reordered);
    },
    []
  );

  const applyPlayableEntry = useCallback(
    async ({
      candidateEntry,
    }: {
      candidateEntry: FeedEntry;
    }): Promise<boolean> => {
      const requestId = ++playbackRequestIdRef.current;
      const playableEntry = await resolveAudioPlayerPlayableEntry(
        candidateEntry
      );

      if (
        requestId !== playbackRequestIdRef.current ||
        !playableEntry?.content?.src
      ) {
        return false;
      }

      setupClientQueue();
      void QueueManager.instance.onTrackStarted(playableEntry);

      setEntry(playableEntry);
      return true;
    },
    [setEntry]
  );

  const handlePlayQueueItem = useCallback(
    async (item: FeedEntry) => {
      const didApplyEntry = await applyPlayableEntry({
        candidateEntry: item,
      });

      if (!didApplyEntry) {
        return;
      }

      hasPendingPreviousAfterRestartRef.current = false;
      const upcoming = await QueueManager.instance.getUpcomingQueue();
      setQueueItems(upcoming);
      setIsPlaying(true);
    },
    [applyPlayableEntry, setIsPlaying]
  );

  const handleNavigatePlaybackContext = useCallback(
    async (direction: QueueTransportDirection): Promise<boolean> => {
      if (!entry) {
        return false;
      }

      setupClientQueue();
      const { entry: adjacentQueueEntry } = await resolveAdjacentQueueEntry({
        entry,
        direction,
      });

      if (adjacentQueueEntry) {
        const didApplyEntry = await applyPlayableEntry({
          candidateEntry: adjacentQueueEntry,
        });

        if (!didApplyEntry) {
          return false;
        }

        hasPendingPreviousAfterRestartRef.current = false;
        setIsPlaying(true);
        const upcoming = await QueueManager.instance.getUpcomingQueue();
        setQueueItems(upcoming);
        return true;
      }

      const siblingEntry = await resolveAdjacentSiblingEntry({
        entry,
        direction,
      });

      if (!siblingEntry?.content?.src && !siblingEntry?.link?.href) {
        return false;
      }

      const didApplyEntry = await applyPlayableEntry({
        candidateEntry: siblingEntry,
      });

      if (!didApplyEntry) {
        return false;
      }

      hasPendingPreviousAfterRestartRef.current = false;
      setIsPlaying(true);
      return true;
    },
    [applyPlayableEntry, entry, setIsPlaying]
  );

  const handleAudioEnded = useCallback(async () => {
    if (!entry) {
      setIsPlaying(false);
      return;
    }

    const didAdvance = await handleNavigatePlaybackContext('next');

    if (!didAdvance) {
      setEntry(null);
      setIsPlaying(false);
    }
  }, [entry, handleNavigatePlaybackContext, setEntry, setIsPlaying]);

  const handlePreviousTrack = useCallback(() => {
    const playbackCurrentTime = audioRef.current?.currentTime ?? currentTime;

    const intent = resolvePreviousTrackIntent({
      currentTime: playbackCurrentTime,
      hasPendingPreviousAfterRestart: hasPendingPreviousAfterRestartRef.current,
    });

    if (intent === 'restart-current') {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }

      hasPendingPreviousAfterRestartRef.current = true;
      setCurrentTime(0);
      return;
    }

    hasPendingPreviousAfterRestartRef.current = false;

    void handleNavigatePlaybackContext('previous');
  }, [audioRef, currentTime, handleNavigatePlaybackContext, setCurrentTime]);

  const handleNextTrack = useCallback(() => {
    hasPendingPreviousAfterRestartRef.current = false;
    void handleNavigatePlaybackContext('next');
  }, [handleNavigatePlaybackContext]);

  const syncFavoriteStateFromStorage = useCallback(async () => {
    if (!entry?.id || !isLoggedIn || !localStorageSuffix) {
      setIsFavorited(false);
      return;
    }

    const favoritesFeed = (await localforage.getItem(
      `favorites-${localStorageSuffix}`
    )) as { entry?: Array<{ id?: string }> } | null;

    const isEntryFavorited = Boolean(
      favoritesFeed?.entry?.some(
        (favoriteEntry) => favoriteEntry.id === entry.id
      )
    );

    setIsFavorited(isEntryFavorited);
  }, [entry?.id, isLoggedIn, localStorageSuffix, setIsFavorited]);

  const handleToggleFavorite = useCallback(() => {
    if (!entry?.id) {
      return;
    }

    if (!isLoggedIn) {
      dispatchAudioPlayerCollapseExpanded();
      navigate(
        `/login?redirectBack=${encodeURIComponent(
          `${location.pathname}${location.search}`
        )}`
      );
      return;
    }

    const nextFavoriteState = !isFavorited;
    const favAction = nextFavoriteState ? 'added' : 'removed';

    setIsFavorited(nextFavoriteState);

    let stringifyEntry = '';

    try {
      stringifyEntry = JSON.stringify(entry);
    } catch {
      setIsFavorited((prev) => !prev);
      return;
    }

    favoriteActionFetcher.submit(
      { favAction, stringifyEntry },
      {
        method: 'post',
        action: '/api/favorite-action',
      }
    );
  }, [
    entry,
    favoriteActionFetcher,
    isFavorited,
    isLoggedIn,
    location.pathname,
    location.search,
    navigate,
    setIsFavorited,
    setIsExpanded,
  ]);

  useEffect(() => {
    if (isExpanded || !isQueueOpen) return;

    setIsQueueOpen(false);
  }, [isExpanded, isQueueOpen, setIsQueueOpen]);

  useEffect(() => {
    const handleCollapseExpandedPlayer = (): void => {
      setIsExpanded(false);
    };

    window.addEventListener(
      AUDIO_PLAYER_COLLAPSE_EXPANDED_EVENT,
      handleCollapseExpandedPlayer
    );

    return () => {
      window.removeEventListener(
        AUDIO_PLAYER_COLLAPSE_EXPANDED_EVENT,
        handleCollapseExpandedPlayer
      );
    };
  }, [setIsExpanded]);

  const activeProfileRef = useRef<string | null>(null);
  const isProfileTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    if (entry) {
      activeProfileRef.current = getStoredProfileString();
      isProfileTrackedRef.current = true;
    } else {
      isProfileTrackedRef.current = false;
    }
  }, [entry]);

  useEffect(() => {
    const handleClosePlayer = (): void => {
      playbackRequestIdRef.current += 1;
      hasPendingPreviousAfterRestartRef.current = false;
      closePlayer();
    };

    const checkProfile = (): void => {
      if (!isProfileTrackedRef.current) return;
      const latestProfile = getStoredProfileString();
      if (latestProfile !== activeProfileRef.current) {
        activeProfileRef.current = latestProfile;
        handleClosePlayer();
      }
    };

    window.addEventListener(AUDIO_PLAYER_CLOSE_EVENT, handleClosePlayer);
    window.addEventListener('storage', checkProfile);
    window.addEventListener(ZAPP_STORAGE_UPDATED_EVENT, checkProfile);

    const intervalId = setInterval(checkProfile, 500);

    return () => {
      window.removeEventListener(AUDIO_PLAYER_CLOSE_EVENT, handleClosePlayer);
      window.removeEventListener('storage', checkProfile);
      window.removeEventListener(ZAPP_STORAGE_UPDATED_EVENT, checkProfile);
      clearInterval(intervalId);
    };
  }, [closePlayer]);

  const handleMiniAddToPlaylist = useCallback((): void => {
    if (!hasAddToPlaylistAction) {
      return;
    }

    showAddToPlaylist(addToPlaylistButtonRef.current);
  }, [hasAddToPlaylistAction, showAddToPlaylist]);

  const handleAddToPlaylistPressStart = useCallback(() => {
    setIsQueueOpen(false);
    setPlaybackSpeedOpen(false);
    setIsSpeedMenuOpen(false);
  }, [setIsQueueOpen, setIsSpeedMenuOpen, setPlaybackSpeedOpen]);

  useEffect(() => {
    if (!isQueueOpen) return;

    void refreshQueueItems();
  }, [isQueueOpen, refreshQueueItems]);

  useEffect(() => {
    setupClientQueue();
  }, []);

  useEffect(() => {
    const onQueueItemsChanged = () => {
      void refreshQueueItems();
    };

    window.addEventListener(QUEUE_CHANGED_EVENT, onQueueItemsChanged);

    return () => {
      window.removeEventListener(QUEUE_CHANGED_EVENT, onQueueItemsChanged);
    };
  }, [refreshQueueItems]);

  useEffect(() => {
    if (!hasQueue || !isQueueOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setIsQueueOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [hasQueue, isQueueOpen, setIsQueueOpen]);

  useEffect(() => {
    const actionBus = ActionBus.getInstance();
    const unsubscribe = actionBus.on((action) => {
      if (action.type !== 'openQueue_success') return;
      if (!hasQueue) return;

      setIsExpanded(true);
      closeSpeedOverlay();
      dismissPopupMenu();
      setIsQueueOpen(true);
    });

    return () => {
      unsubscribe();
    };
  }, [
    closeSpeedOverlay,
    dismissPopupMenu,
    hasQueue,
    setIsExpanded,
    setIsQueueOpen,
  ]);

  useEffect(() => {
    const actionBus = ActionBus.getInstance();
    const unsubscribe = actionBus.on((action) => {
      if (
        !getIsAddToPlaylistMutationSuccess({
          action,
          entryId: entry?.id,
        })
      ) {
        return;
      }

      setIsPlaylistAdded(true);
    });

    return () => {
      unsubscribe();
    };
  }, [entry?.id, setIsPlaylistAdded]);

  useEffect(() => {
    void syncFavoriteStateFromStorage();
  }, [favoriteActionFetcher.data, syncFavoriteStateFromStorage]);

  if (!entry?.content?.src) return null;

  const audioEl = (
    <audio
      ref={audioRef}
      onPlay={() => setIsPlaying(true)}
      onPause={() => setIsPlaying(false)}
      onEnded={() => {
        void handleAudioEnded();
      }}
      onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
      onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
      className="hidden"
    >
      <source src={entry.content.src} type={resolveAudioMimeType(entry)} />
    </audio>
  );

  return (
    <div
      className={`audio-player-vars-${audioPlayerConfig?.id} audio-player-queue-vars audio-player-responsive_vars pointer-events-none fixed inset-x-0 bottom-0 z-50`}
      style={playbackButtonInteractionStyle}
    >
      <div className="relative z-[999]">
        <ExpandedAudioPlayer
          entry={entry}
          imageSrc={fullImageSrc}
          playlistName={fullPlaylistName}
          title={fullTitle}
          artist={fullArtist}
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          remaining={remaining}
          playbackRate={playbackRate}
          speedOptions={speedOptions}
          hasSpeedControl={hasSpeedControl}
          hasQueue={hasQueue}
          isMuted={isMuted}
          volume={volume}
          isQueueOpen={isQueueOpen}
          isSpeedMenuOpen={isSpeedMenuOpen}
          isFavorited={isFavorited}
          isPlaylistAdded={isPlaylistAdded}
          showFavoriteMobile={showFullFavoriteMobile}
          iconAssets={iconAssets}
          queueTitle={queueTitle}
          progressColor={barProgressColor}
          totalBarColor={barTotalColor}
          handleColor={barHandleColor}
          elapsedLabelColor={elapsedLabelColor}
          totalLabelColor={totalLabelColor}
          fullMaxWidthTablet={fullMaxWidthTablet}
          fullImageCornerRadius={fullImageCornerRadius}
          fullImagePlaceholderColor={fullImagePlaceholderColor}
          fullImageBorderColor={fullImageBorderColor}
          fullContainerClassName={fullContainerClassName}
          fullTextGutter={fullTextGutter}
          titleTextStyle={fullTitleTextStyle}
          artistTextStyle={fullArtistTextStyle}
          playlistTextStyle={fullPlaylistTextStyle}
          timeLabelTextStyle={timeLabelTextStyle}
          formatTime={formatTime}
          onPrevious={handlePreviousTrack}
          onTogglePlayback={togglePlayback}
          onNext={handleNextTrack}
          onSkipBack={handleSkipBack}
          onSkipAhead={handleSkipAhead}
          onToggleMute={toggleMute}
          onToggleFavorite={handleToggleFavorite}
          onToggleAddToPlaylist={() => {}}
          onToggleQueue={handleToggleQueueExclusive}
          onToggleSpeedMenu={() => setIsSpeedMenuOpen((prev) => !prev)}
          onOpenMoreMenu={handleOpenMoreMenuExclusive}
          onSelectPlaybackRate={selectPlaybackRate}
          onVolumeChange={handleVolumeChange}
          onClose={() => setIsExpanded(false)}
          onSeek={handleSeek}
          playbackSpeedOpen={playbackSpeedOpen}
          setPlaybackSpeedOpen={setPlaybackSpeedOpenExclusive}
          isExpanded={isExpanded}
        />
      </div>

      <div className="relative z-[888] desktop:z-[9999]">
        <MiniAudioPlayer
          entry={entry}
          title={miniTitle}
          artist={miniArtist}
          imageSrc={miniImageSrc}
          hasQueue={hasQueue}
          hasSpeedControl={hasSpeedControl}
          playbackRate={playbackRate}
          speedOptions={speedOptions}
          isQueueOpen={isQueueOpen}
          isMuted={isMuted}
          volume={volume}
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          remaining={remaining}
          titleColor={miniTitleColor}
          artistColor={miniArtistColor}
          titleTextStyle={miniTitleTextStyle}
          artistTextStyle={miniArtistTextStyle}
          timeLabelTextStyle={timeLabelTextStyle}
          imageCornerRadius={miniImageCornerRadius}
          imagePlaceholderColor={miniImagePlaceholderColor}
          imageBorderColor={miniImageBorderColor}
          containerClassName={miniContainerClassName}
          progressColor={barProgressColor}
          totalBarColor={barTotalColor}
          handleColor={barHandleColor}
          elapsedLabelColor={elapsedLabelColor}
          totalLabelColor={totalLabelColor}
          miniProgressWidth={miniProgressWidth}
          miniSideContainerWidth={miniSideContainerWidth}
          showFavoriteDesktop={showMiniFavoriteDesktop}
          showAddDesktop={showMiniAddDesktop}
          isFavorited={isFavorited}
          isPlaylistAdded={isPlaylistAdded}
          iconAssets={iconAssets}
          formatTime={formatTime}
          onSeek={handleSeek}
          onExpand={() => setIsExpanded(!isExpanded)}
          onPrevious={handlePreviousTrack}
          onTogglePlayback={togglePlayback}
          onNext={handleNextTrack}
          onSkipBack={handleSkipBack}
          onSkipAhead={handleSkipAhead}
          onToggleMute={toggleMute}
          onVolumeChange={handleVolumeChange}
          onToggleQueue={handleToggleQueueExclusive}
          onSelectPlaybackRate={selectPlaybackRate}
          onClose={closePlayer}
          onToggleFavorite={handleToggleFavorite}
          onToggleAddToPlaylist={handleMiniAddToPlaylist}
          onAddToPlaylistPressStart={handleAddToPlaylistPressStart}
          addToPlaylistButtonRef={addToPlaylistButtonRef}
          playbackSpeedOpen={playbackSpeedOpen}
          setPlaybackSpeedOpen={setPlaybackSpeedOpenExclusive}
        >
          {audioEl}
        </MiniAudioPlayer>
      </div>

      <QueuePanel
        isOpen={hasQueue && isQueueOpen && isExpanded}
        labels={{
          queueTitle,
          nowPlayingLabel: queuePanelLabels.nowPlayingLabel,
          nextUpTitle: queuePanelLabels.nextUpTitle,
          nextUpEmptyLabel: queuePanelLabels.nextUpEmptyLabel,
          clearQueueButtonLabel: queuePanelLabels.clearQueueButtonLabel,
        }}
        assets={queuePanelAssets}
        itemConfig={queuePanelItemConfig}
        nowPlayingConfig={{
          imageSrc: fullImageSrc,
          mobileImageDataKey: queuePanelNowPlayingConfig.mobileImageDataKey,
          desktopImageDataKey: queuePanelNowPlayingConfig.desktopImageDataKey,
          mobileImagePlaceholderColor:
            queuePanelNowPlayingConfig.mobileImagePlaceholderColor,
          desktopImagePlaceholderColor:
            queuePanelNowPlayingConfig.desktopImagePlaceholderColor,
          mobileLabelEnabled: queuePanelNowPlayingConfig.mobileLabelEnabled,
          desktopLabelEnabled: queuePanelNowPlayingConfig.desktopLabelEnabled,
          mobileSubtitleEnabled:
            queuePanelNowPlayingConfig.mobileSubtitleEnabled,
          desktopSubtitleEnabled:
            queuePanelNowPlayingConfig.desktopSubtitleEnabled,
          mobileEqualizerAsset: queuePanelNowPlayingConfig.mobileEqualizerAsset,
          desktopEqualizerAsset:
            queuePanelNowPlayingConfig.desktopEqualizerAsset,
        }}
        textTransforms={queuePanelTextTransforms}
        isPlaying={isPlaying}
        currentTitle={fullTitle}
        currentSubtitle={fullArtist}
        queueItems={queueItems}
        closeAsset={iconAssets.close}
        onClose={() => setIsQueueOpen(false)}
        onClearQueue={handleClearQueue}
        onRemoveQueueItem={handleRemoveQueueItem}
        onPlayQueueItem={handlePlayQueueItem}
        onReorderQueueItems={handleReorderQueueItems}
        onTogglePlayback={togglePlayback}
      />
    </div>
  );
}
